/* eslint-disable no-unused-vars */
/* eslint-disable no-param-reassign */
import { ErrorMessage } from '@/@core/utils/wordings'
import { isObjectEmpty } from '@/plugins/custom'
import { decryptData, encryptData, getEncryptionKey } from '@/plugins/storage-encryption'
import { fullEncounters } from '@/store'
import dayjs from 'dayjs'
import cloneDeep from 'lodash/cloneDeep'
import { v4 as uuidv4 } from 'uuid'
import Vue from 'vue'
import { getField, updateField } from 'vuex-map-fields'

const initialState = {
  error: false,
  syncing: false,
  syncingPos: 0,
  syncingCnt: 1,
  syncingTxt: 'Communicating...',
  isSynced: false,
  baseDataLoaded: false,
  items: [],
  baseData: [],
  apiDetails: {},
  isLastPullSuccess: false,
  lastPullTimestamp: null,
}

function isObject(candidate) {
  return (typeof candidate === 'object' || typeof candidate === 'function') && (candidate !== null)
}

function removeJoinData(object) {
  const filtered = { ...object }
  Object.keys(filtered).forEach(key => {
    if (key === '_joinData') {
      delete filtered[key]
    } else if (isObject(filtered[key])) {
      filtered[key] = removeJoinData(filtered[key])
    }
  })

  return filtered
}

function filterQuickEntries(items, search) {
  const today = dayjs().format('YYYY-MM-DD')

  return items.filter(item => {
    if (item.deleted || !item.is_quick_entry) return false
    if (search.user_id && item.created_by_user_id !== search.user_id) return false

    if (search.facility && item.place_of_service_id !== search.facility) return false
    if (search.visit_type && item.visit_type !== search.visit_type) return false
    if (search.synced !== null && item.is_synced !== search.synced) return false

    const log = item.communication_log || {}
    const isLogEmpty = isObjectEmpty(log)
    const visitDate = item.visit_date || null

    if (!visitDate || !dayjs(visitDate).isValid()) return false
    if (search.visit_date && !search.last_fourteen_days && visitDate !== search.visit_date) return false
    if (item.visit_date !== today && (isLogEmpty || log.visit_date !== item.visit_date)) return false
    if (search.last_fourteen_days && (dayjs().diff(visitDate, 'days') > 14 )) return false
    if (search.logStatus && !isLogEmpty && search.logStatus !== log.log_status) return false

    const hasValidPatientIds = Array.isArray(search.patients_ids) && search.patients_ids.length > 0
    const isPatientIncluded = search.patients_ids?.includes(item.patient_id)

    if (hasValidPatientIds && !isPatientIncluded) return false

    return true
  })
}
function filterEncounters(items, search) {
  return items.filter(item => {
    if (item.deleted) return false
    if (search.omitDeferredCare && item.is_deferred_care) return false
    if (search.user_id && item.created_by_user_id !== search.user_id) return false
    if (search.facility && item.place_of_service_id !== search.facility) return false
    if (search.visit_type && item.visit_type !== search.visit_type) return false
    if (search.is_signed !== null && search.is_signed !== undefined && item.is_signed !== search.is_signed) return false
    if (search.is_synced !== null && search.is_synced !== undefined && item.is_synced !== search.is_synced) return false
    if (search.last_fourteen_days && dayjs().diff(item.visit_date, 'days') > 14) return false
    if (search.visit_date && !search.last_fourteen_days && (!dayjs(search.visit_date_us).isValid() || item.visit_date !== search.visit_date)) return false
    if (search.practice_type_id && item.practice_type_id !== search.practice_type_id) return false

    const hasValidPatientIds = Array.isArray(search.patients_ids) && search.patients_ids.length > 0
    const isPatientIncluded = search.patients_ids?.includes(item.patient_id)

    if (hasValidPatientIds && !isPatientIncluded) return false
    return true
  })
}

export default {
  namespaced: true,
  state: {
    ...initialState,
  },
  getters: {
    getField,
    getLength: state => state.items.length,
    getLastId: state => (state.items.length > 0 ? state.items[state.items.length - 1].id : false),
    syncing: state => state.syncing,
    syncingPos: state => state.syncingPos,
    syncingCnt: state => state.syncingCnt,
    syncingTxt: state => state.syncingTxt,
    isSynced: state => state.isSynced,
    isLastPullSuccess: state => state.isLastPullSuccess,
    lastPullTimestamp: state => state.lastPullTimestamp,
    encountersToSync: state => state.items.filter(encounter => (!encounter.is_synced)),
    baseDataLoaded: state => state.baseDataLoaded,
    abdominalExams: state => state.baseData.abdominalExams,
    antibioticRoutes: state => state.baseData.antibioticRoutes,
    billingStatuses: state => state.baseData.billingStatuses,
    bodyAreas: state => state.baseData.bodyAreas,
    chronicWounds: state => state.baseData.chronicWounds,
    continenceOptions: state => state.baseData.continence,
    credentialPayers: state => state.baseData.credentialPayers,
    debridementNotPerformedReasons: state => state.baseData.debridementNotPerformedReasons,
    dermEtiologies: state => state.baseData.dermEtiologies,
    dressings: state => state.baseData.dressings,
    durations: state => state.baseData.durations,
    exudateAmounts: state => state.baseData.exudateAmounts,
    exudateTypes: state => state.baseData.exudateTypes,
    followUpActivities: state => state.baseData.followUpActivities,
    followUpIntervals: state => state.baseData.followUpIntervals,
    footwearNotableIssues: state => state.baseData.footwearNotableIssues,
    gTubeTypes: state => state.baseData.gTubeTypes,
    genders: state => state.baseData.genders,
    generalAppearances: state => state.baseData.generalAppearances,
    goals: state => state.baseData.goals,
    healthChanges: state => state.baseData.healthChanges.sort((a, b) => {
      if (a.title === 'No change since last visit') return -1
      if (b.title === 'No change since last visit') return 1

      return 0
    }),
    infectionSigns: state => state.baseData.infectionSigns.sort((a, b) => {
      if (a.title === 'None') return -1
      if (b.title === 'None') return 1

      return 0
    }),
    labOptions: state => state.baseData.labs,
    lesionConditions: state => state.baseData.lesionConditions,
    mattresses: state => state.baseData.mattresses,
    mattressTypes: state => state.baseData.mattressTypes,
    medicalHistories: state => state.baseData.medicalHistories,
    medications: state => state.baseData.medications,
    mentalStatuses: state => state.baseData.mentalStatuses,
    mobilityOptions: state => state.baseData.mobility,
    months: state => state.baseData.months,
    neuropathyTypes: state => state.baseData.neuropathyTypes,
    neuropathySeverities: state => state.baseData.neuropathySeverities,
    odors: state => state.baseData.odors,
    offloadings: state => state.baseData.offloadings,
    periTubeFindings: state => state.baseData.periTubeFindings,
    periTubeTracts: state => state.baseData.periTubeTracts,
    periwounds: state => state.baseData.periwounds,
    preoperativeIndications: state => state.baseData.preoperativeIndications,
    opNoteBloodLosses: state => state.baseData.opNoteBloodLosses,
    opNoteCleansings: state => state.baseData.opNoteCleansings,
    opNoteHemostases: state => state.baseData.opNoteHemostases,
    opNoteIncisionTypes: state => state.baseData.opNoteIncisionTypes,
    opNoteInstruments: state => state.baseData.opNoteInstruments,
    opNotePainManagements: state => state.baseData.opNotePainManagements,
    opNotePositionings: state => state.baseData.opNotePositionings,
    opNoteVisualizedBones: state => state.baseData.opNoteVisualizedBones,
    opNoteVisualizedMuscles: state => state.baseData.opNoteVisualizedMuscles.sort((a, b) => {
      if (a.title === 'None') return -1
      if (b.title === 'None') return 1

      return a.title < b.title ? -1 : a.title > b.title ? 1 : 0
    }),
    prognoses: state => state.baseData.prognoses,
    progresses: state => state.baseData.progresses,
    reasonForOngoingDebridement: state => state.baseData.reasonForOngoingDebridement,
    replacementReasons: state => state.baseData.replacementReasons,
    riskFactors: state => state.baseData.riskFactors,
    socialHistoryOptions: state => state.baseData.socialHistories,
    temperatureUnits: state => state.baseData.temperatureUnits,
    testResults: state => state.baseData.testResults,
    testTypes: state => state.baseData.testTypes,
    topicalAgents: state => state.baseData.topicalAgents,
    verificationOfPlacements: state => state.baseData.verificationOfPlacements,
    visitLocations: state => state.baseData.visitLocations,
    visitTypes: state => state.baseData.visitTypes,
    woundEdges: state => state.baseData.woundEdges,
    woundEtiologies: state => state.baseData.woundEtiologies,
    woundStages: state => state.baseData.woundStages,
    appearances: state => state.baseData.appearances,
    orientations: state => state.baseData.orientations,
    memories: state => state.baseData.memories,
    intellects: state => state.baseData.intellects,
    concentrations: state => state.baseData.concentrations,
    insights: state => state.baseData.insights,
    judgements: state => state.baseData.judgements,
    behaviors: state => state.baseData.behaviors,
    speeches: state => state.baseData.speeches,
    thoughtFlows: state => state.baseData.thoughtFlows,
    moods: state => state.baseData.moods,
    affects: state => state.baseData.affects,
    strengths: state => state.baseData.strengths,
    limitations: state => state.baseData.limitations,
    impairedFunctions: state => state.baseData.impairedFunctions,
    logStatus: state => state.baseData.logStatus,
    logType: state => state.baseData.logType,
    filteredEncountersBySearch: state => search => {
      return filterEncounters(state.items, search)
    },
    filteredQuickEntriesBySearch: state => search => filterQuickEntries(state.items, search),
    getPatientsIdFromQuickEntrySearch: state => search => filterQuickEntries(state.items, search).map(item => item.patient_id),
    getById(state) {
      return (id, idField = 'id') => state.items.find(item => item[idField] === id) || {}
    },
    getIsSignedById(state) {
      return (id, idField = 'id') => {
        const item = state.items.find(item => item[idField] === id)
        return item?.is_signed === true
      }
    },
    getVisitDatedById(state) {
      return (id, idField = 'id') => {
        const item = state.items.find(item => item[idField] === id)
        return item?.visit_date
      }
    },
    getIndexById: state => id => state.items.findIndex(x => x.id === id),
    isDuplicateEncounter: state => (encounter, authUserId) => {
      const index = state.items
        .findIndex(x => (
          x.created_by_user_id === authUserId
          && x.created_by_user_id === encounter.created_by_user_id
          && x.visit_date === encounter.visit_date
          && x.patient_id === encounter.patient_id
          && x.practice_type_id === encounter.practice_type_id
          && x.deleted !== true
        ))
      if (index === -1) return false

      return state.items[index].id
    },
    isPriorEncounter: state => encounter => {
      const index = state.items
        .findIndex(x => (
          x.created_by_user_id === encounter.created_by_user_id
          && x.place_of_service_id === encounter.place_of_service_id
          && x.visit_date >= encounter.visit_date
          && x.patient_id === encounter.patient_id
          && x.practice_type_id === encounter.practice_type_id
          && x.id !== encounter.id
          && x.deleted !== true
        ))
      if (index === -1) return false

      return !!state.items[index]
    },

    // Retrieves all signed encounters from a patient
    patientHistory: state => (patientId) => state.items
      .filter(x => (
        x.patient_id === patientId
        && x.is_signed
        && x.deleted !== true
      )).sort((a, b) => (a.visit_date < b.visit_date && 1) || -1),

    /*
      Used only in encounters/Create.vue to decide if it's a new or follow-up visit.
      So there's no current encounter ID (currentId) to exclude.
      Probably shouldn't use this getter for other uses, use previousEncounters() instead.
    */
    previousEncounterId: state => (patientId, practiceTypeId, newVisitDate) => {
      const index = state.items
        .sort((a, b) => (a.visit_date <= b.visit_date && 1) || -1)
        .findIndex(x => (
          x.patient_id === patientId
          && x.practice_type_id === practiceTypeId
          && x.deleted !== true

          // Removed by SWC-850: && x.is_signed
        ))

      // If previous encounter is in the future or > 36 months, ignore
      if (index === -1
        // 20241001 JMC: TEMP: Disabling this because it causes issues in T4
        // where patients cannot have more than one NEW type visit.
        //   || dayjs().diff(state.items[index].visit_date, 'month') > 36
      ) return false

      return state.items[index].id
    },

    /*
      Used only in encounters/Create.vue to decide if it's a new or follow-up visit.
      So there's no current encounter ID (currentId) to exclude.
      Probably shouldn't use this getter for other uses, use previousEncounters() instead.
    */
    previousEncounter: state => (patientId, practiceTypeId, encounterId) => {
      const previousEncounter = state.items
        .filter(x =>
          x.patient_id === patientId
          && x.practice_type_id === practiceTypeId
          && x.deleted !== true
          && !x.is_deferred_care
          && x.id !== encounterId
          // Removed by SWC-850: && x.is_signed
        )
        .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date))[0]

      // 20250321 JMC: Disabled this to simplify, since we already have a validation to not allow visits in the future.
      // If no valid previous encounter or encounter is in the future, return false
      // if (!previousEncounter ||
      //   dayjs(newVisitDate).diff(previousEncounter.visit_date, 'day') < 1

        // 20241001 JMC: TEMP: Disabling this because it causes issues in T4
        // where patients cannot have more than one NEW type visit.
        // || dayjs().diff(previousEncounter.visit_date, 'month') > 36
      // ) {
      //   return false
      // }
      return previousEncounter || false
    },

    // Probably should use this getter to find previous encounters
    previousEncounters: state => (patientId, practiceTypeId, currentId, days) => state.items
      .filter(x => (
        x.patient_id === patientId
        && x.practice_type_id === practiceTypeId
        && x.id !== currentId
        && x.is_signed
        && x.deleted !== true
        && !x.is_deferred_care
        && dayjs().diff(x.visit_date, 'days') <= days
      )).sort((a, b) => (a.visit_date < b.visit_date && 1) || -1),

    // Getter for retrieving any signed or unsigned encounter, possible temp duplicate
    previousSignedUnsignedEncounters: state => (patientId, practiceTypeId, currentId, date = null) => state.items
      .filter(note => (
        note.patient_id === patientId
        && note.practice_type_id === practiceTypeId
        && note.id !== currentId
        && note.deleted !== true
        && (date ? note.visit_date < date : true)
      )).sort((a, b) => (a.visit_date < b.visit_date && 1) || -1),

    unsyncedEncounters: state => (patientId, practiceTypeId, currentId, days) => state.items
      .filter(note => (
        note.patient_id === patientId
      && note.practice_type_id === practiceTypeId
      && note.id !== currentId
      && note.deleted !== true
      && note.is_synced !== true
      && dayjs().diff(note.visit_date, 'days') <= days
      )).sort((a, b) => (a.visit_date < b.visit_date && 1) || -1),

    getCalloutByPatient: state => patientId => state.items.filter(encounter => (
      encounter.patient_id === patientId && encounter.billing_callout !== undefined
    )),

    // Return last signed encounter of a patient.
    lastSignedEncounter: state => patientId => {
      const encounter = state.items
        .filter(x => x.patient_id === patientId && x.deleted !== true && x.is_signed && !x.is_deferred_care)
        .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date))[0]

      return encounter || false
    },
    // Return ID of last signed encounter for a patient
    lastSignedEncounterId: state => patientId => {
      const encounter = state.items
        .filter(x => x.patient_id === patientId && x.deleted !== true && x.is_signed && !x.is_deferred_care)
        .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date))
        .find(() => true) // get the first item after sorting

      return encounter?.id || null
    },

    // Get PMH and risk factors from previously signed note.
    previousPmhAndRiskFactors: state => (patientId, currentEncounterId, cutoffDate) => {
      const previous = state.items
        .filter(encounter =>
          encounter.patient_id === patientId &&
          encounter.id !== currentEncounterId &&
          encounter.is_signed &&
          encounter.deleted !== true &&
          !encounter.is_deferred_care &&
          dayjs(encounter.visit_date).isBefore(cutoffDate)
        )
        .sort((a, b) => dayjs(b.visit_date).diff(dayjs(a.visit_date)))[0]

      return previous
        ? {
          medical_histories: previous.medical_histories || [],
          medical_histories_other: previous.medical_histories_other || '',
          risk_factors: previous.risk_factors || [],
          risk_factors_other: previous.risk_factors_other || ''
        }
        : null
    },
  },
  mutations: {
    lastPullTimestamp: (state, value) => {
      state.lastPullTimestamp = value
    },
    isLastPullSuccess: (state, bool) => {
      state.isLastPullSuccess = bool
    },
    DEBUG__deleteEncounters: (state, userID) => {
      const localEncounters = state.items.filter(encounter => (
        !encounter.is_synced
            && encounter.created_by_user_id === userID
      ))

      const newStateItems = state.items
      for (let i = localEncounters.length - 1; i >= 0; i--) {
        state.items.splice(newStateItems.findIndex(index => index.id === localEncounters[i].id), 1)
      }
      state.isSynced = false
    },

    updateField,
    updateCommLog: (state, { encounterIds, commLogData }) => {
      // Update the communication_log property for each encounter with the specified IDs
      encounterIds.forEach(encounterId => {
        const index = state.items.findIndex(x => x.id === encounterId)

        if (index !== -1) {
          state.items[index] = {
            ...state.items[index],

            // Update the encounter's communication_log_id
            communication_log_id: commLogData.id,

            // Set the encounter's related communication_log
            communication_log: { ...commLogData },

            // Mark as not synced since we still have to push the updated communication_log_id to the server on the user's next sync.
            is_synced: false,
          }
        }
      })

      // Set synced status to false
      state.isSynced = false
    },
    addEncounter: (state, value) => {
      const newValue = {
        ...value,
        id: uuidv4(),
        is_synced: false,
      }
      state.items.push(newValue)

      // Set synced status to false
      state.isSynced = false
    },
    addEncounters: (state, value) => {
      value.forEach(note => {
        state.items.push(note)
      })

      // Set synced status to false
      state.isSynced = false
    },
    loadEncounters: (state, value) => {
      value.forEach(note => {
        state.items.push(note)
      })
    },
    updateEncounter: (state, data) => {
      const patientsToUpdate = []
      let encounterArr = Array.isArray(data) ? data : [data]
      encounterArr.forEach(e => {
        // Find the encounter index
        const index = state.items.findIndex(x => x.id === e.encounter.id || x.id === e.id)

        // Update encounter values
        if (index !== -1) state.items[index] = { ...e.encounter }

        /// If patient data is included, update the patient (patient includes wound treatments)
        if (e.patient && e.patient?.id) patientsToUpdate.push(e.patient)
      })
      if (patientsToUpdate.length !== 0) Vue.store.commit('patients/updatePatient', patientsToUpdate)

      // Set synced status to false
      state.isSynced = false
    },
    updateEncounterAttachments: (state, { id = false, attachments = [] }) => {
      // Find the encounter index
      const index = state.items.findIndex(x => x.id === id)

      // Update encounter attachments
      if (index !== -1) {
        state.items[index].encounter_attachments = [...attachments]
        state.items[index].is_synced = false
      }

      // Set synced status to false
      state.isSynced = false
    },
    deleteEncounter: (state, value) => {
      state.items = state.items.filter(x => x.id !== value.id)

      // Set synced status to false
      state.isSynced = false
    },
    encounterChanged: (state, value) => {
      // Find the encounter index
      const index = state.items.findIndex(x => x.id === value.id)
      if (index !== -1) {
        state.items[index].is_validated = false
        state.items[index].is_synced = false
      }

      // Set synced status to false
      state.isSynced = false
    },
    synced: (state, value) => {
      state.isSynced = value
    },
    RESET_STATE: state => {
      Object.keys(initialState).forEach(key => {
        state[key] = initialState[key]
      })
    },
    SET_ENCOUNTERS(state, data) {
      Vue.set(state, 'items', data)
    },
    SET_BASE_DATA: (state, data) => {
      state.baseData = data

      // Topical Agents is a bit unique, so we'll reformat the data
      const topicalAgents = []
      data.topicalAgents.forEach((value, index) => {
        topicalAgents[index] = value.title
      })
      state.baseData.topicalAgents = topicalAgents

      // Visualized Muscles is a bit unique, so we'll reformat the data
      data.opNoteVisualizedMuscles.forEach((value, index) => {
        if (value.title.length > 10 && value.title.substring(0, 10) === '{{HEADER}}') {
          data.opNoteVisualizedMuscles[index] = {
            id: value.id,
            header: value.title.substring(10),
          }
        }
      })
      state.baseData.opNoteVisualizedMuscles = data.opNoteVisualizedMuscles

      state.baseDataLoaded = true
    },
    SET_SYNCING: (state, value) => {
      state.syncing = value
    },
    SET_SYNCING_POS: (state, value) => {
      state.syncingPos = value
    },
    SET_SYNCING_CNT: (state, value) => {
      state.syncingCnt = value
    },
    SET_SYNCING_TXT: (state, value) => {
      state.syncingTxt = value
    },
    SET_SYNCED: (state, value) => {
      state.isSynced = value
    },
    SET_API_DETAILS: (state, data) => {
      state.apiDetails = data
      console.log(`Total encounters synced: ${data.items}`)
      console.log(`Query: ${data.query_time}s, Optimize: ${data.optimize_time}s`)
    },

    // Mutation to load full data into state
    LOAD_DATA(state, fullEncountersData ) {
      const encounterIndex = state.items.findIndex(item => item.id === fullEncountersData.id)
      if (encounterIndex !== -1) {
        state.items[encounterIndex] = { ...fullEncountersData, is_loaded: true }
      }

    },
  },
  actions: {
    async loadEncounterData({ commit, state }, patientId) {
      try {
        // Find the encounters for the specified patient ID in the state
        let encounters = state.items.filter(item => item.patient_id === patientId)

        if (encounters && encounters.length > 0) {
          let fullEncountersData = null
          const key = await getEncryptionKey()
          // Loop through each encounter and update if not loaded
          for (const encounter of encounters) {
            if (!encounter.is_loaded) {
              // Fetch fullEncounters data from IndexedDB
              fullEncountersData = await fullEncounters.getItem(encounter.id)

              if (fullEncountersData) {
                // Decrypt data if encrypted
                if (fullEncountersData.iv) {
                  fullEncountersData = await decryptData(key, fullEncountersData.iv, fullEncountersData.encrypted)
                }
                Vue.prototype.$custom.freezeSignedEncounters(fullEncountersData)
                // Commit the mutation to load full encounter data.
                commit('LOAD_DATA', fullEncountersData)
              }
            }
          }
          fullEncountersData = null
        }
        encounters = null
      } catch (error) {
        console.error('Error loading encounter data:', error)
      }
    },

    syncingModal({ commit, state }, value) {
      if (value) {
        commit('SET_SYNCING', true)
      } else {
        const timeout = Math.max(0, (1 - state.syncingPos / state.syncingCnt) * 400) + 200
        commit('SET_SYNCING_TXT', 'Communicating...')
        commit('SET_SYNCING_POS', 1)
        commit('SET_SYNCING_CNT', 1)
        setTimeout(() => {
          commit('SET_SYNCING', false)
          commit('SET_SYNCING_POS', 0)
        }, timeout)
      }
    },
    syncingProgress({ commit }, value) {
      commit('SET_SYNCING_POS', value.pos)
      commit('SET_SYNCING_CNT', value.cnt)
    },
    setSynced({ commit }, value) {
      commit('SET_SYNCED', value)
    },
    syncEncounter({ commit, dispatch }, {
      data,
      retries,
    }) {
      const encountersToSync = []
      const encounterArr = Array.isArray(data) ? cloneDeep(data) : [cloneDeep(data)]
      encounterArr.forEach(encounter => {
        encountersToSync.push({
        // Remove all '_joinData' objects
          ...removeJoinData(encounter),

          // Filter out the already synced attachments
          encounter_attachments: encounter?.encounter_attachments
            ? encounter.encounter_attachments?.filter(x => x.updated === true)
            : [],
        })
      })

      return Vue.axios.post('encounters.json', encountersToSync)
        .then(response => {
          if (response.data.responseData === "Request payload is empty or invalid.") {
            throw new Error("Request payload is empty or invalid. Please check your internet connection and try to sync again. If the problem persists, contact IT Support.")
          } else if (response.data.responseData.includes('Server is currently busy')) {
            throw new Error(response.data.responseData)
          } else {
            return response.data
          }
        })
        .catch(e => {
          // Retry if there are remaining attempts
          if (retries > 0) {
            const retryCount = retries-1
            console.log(`Retrying... Attempts left: ${retryCount}`)
            return dispatch('syncEncounter', { data: data, retries: retryCount })
          } else {
            // Process the error after all retry attempts are exhausted
            return Vue.prototype.$custom.processCommError(e, 'Error on Upload Encounter')
          }
        })
    },
    sendCommLog({ commit }, commLog) {
      return Vue.axios.post('communication-logs.json', commLog)
        .then(response => {
          if (response.data.status === 'Success') {
            // Destructure the 'encounters' property from 'response.data.data'
            // and gather the remaining properties into the 'commLogData' object.
            const { encounters, ...commLogData } = response.data.data

            // Extract the 'id' property from each encounter in the 'encounters' array.
            const encounterIds = response.data.data.encounters.map(encounter => encounter.id)

            // Commit to state
            commit('updateCommLog', { encounterIds, commLogData })

            return true
          }
          throw response.data.message
        })
        .catch(error => {
          Vue.prototype.$custom.processCommError(error, 'send communication log')
          throw error
        })
    },
    async sendPasswordThroughSms({ commit }, payLoad) {
      try {
        const response = await Vue.axios.post(`gateways/send-sms`, payLoad);
        if ([200, 201].includes(response.status)) {
          Vue.prototype.$custom.showSuccessNotification('Password sent through sms')
          return true
        }
      } catch (error) {
        console.log(error)
        const message = error.response?.data?.message || ErrorMessage

        Vue.prototype.$custom.processCommError({ message }, 'sending password through sms', true)
      }
    },
    getEstimatedPayment({ commit }, id) {
      return Vue.axios.get(`/encounters/${id}.json`)
        .then(response => {
          if (response.data.status === 'Success') {
            const encounter = response.data.encounter
            return {
              estimated_payment: encounter.estimated_payment,
              pay_total_amnio: encounter.pay_total_amnio
            }
          } else {
            return false
          }
        })
        .catch(e => Vue.prototype.$custom.processCommError(e, 'get estimated payment'));
    },
    async loadEncounters({ commit }) {
      console.log('Getting encounters...')
      commit('SET_SYNCED', false)
      let userId = Vue.auth.user().id
      const maxAttempts = 3
      let items = 0
      let queryTime = 0
      let optimizeTime = 0
      const key = await getEncryptionKey()
      await indexedDB.deleteDatabase('full-encounters')
      await commit('SET_ENCOUNTERS', [])
      // Flag last encounters sync pull as unsuccessful
      commit('isLastPullSuccess', false)
      commit('encounters/SET_SYNCING_TXT', 'Fetching encounter data', { root: true })

      // Reset scribe mode:
      commit('auth/SET_SCRIBE_PROVIDER', null, { root: true })

      // Fetch records in batches
      const fetchPage = async (page, attempt = 1) => {
        try {
          const response = await Vue.axios.get(`encounters.json?page=${page}`)
          if (response.data.status === 'Success') {
            const { totalPages } = response.data

            commit('encounters/SET_SYNCING_TXT', `Fetching encounter data: page ${page} of ${totalPages}`, { root: true })
            console.log(`GET encounters progress: page ${page} of ${totalPages}`)

            // If the backend included a scribeProvider object, store it in auth state
            if (response.data.scribeProvider) {
              commit('auth/SET_SCRIBE_PROVIDER', response.data.scribeProvider, { root: true })
              // Change user id reference to the scribe's provider ID.
              userId = response.data.scribeProvider.id
            }

            // Process encounters
            // eslint-disable-next-line no-restricted-syntax
            for (let item of response.data.encounters) {
              // Preload user's valid quick entry records.
              if (
                item.created_by_user_id === userId
                && item.is_quick_entry
                && item.visit_date === Vue.prototype.$date().format('YYYY-MM-DD')
              ) {
                item.is_loaded = true
                Vue.store.dispatch('patients/loadPatientData', item.patient_id)
              } else {
              // Encrypt the encounter data before storing in idb.
                const encryptedData = await encryptData(key, item)

                // Store the data in full-encounters
                await fullEncounters.setItem(item.id, encryptedData)

                // User's other encounters.
                if (item.created_by_user_id === userId) {
                  // Modify the encounterData to load only the necessary values into memory.
                  // We can pull other properties from full-encounters as needed.
                  delete item.encounter_active_wound_infection
                  delete item.encounter_covid_assessment
                  delete item.encounter_pulse_assessment
                  delete item.assessment_plan
                  delete item.addendums
                  delete item.footwear_notable_issues
                  delete item.labs
                  delete item.medical_histories
                  delete item.medications
                  delete item.risk_factors
                  delete item.social_histories
                  delete item.body_areas
                  delete item.general_appearances
                } else {
                  // Encounters that don't belong to the user.
                  // Remove all properties except 'id', 'patient_id', and 'is_synced' for notes from other users.
                  Object.keys(item).forEach(encounterKey => {
                    if (encounterKey !== 'id' && encounterKey !== 'patient_id' && encounterKey !== 'is_synced') {
                      delete item[encounterKey]
                    }
                  })
                }
              }
            }

            await commit('loadEncounters', response.data.encounters)
            items += response.data.details.items
            queryTime += parseFloat(response.data.details.query_time)
            optimizeTime += parseFloat(response.data.details.optimize_time)

            // If there are more pages, fetch the next page recursively
            if (response.data.hasMorePages) {
              return fetchPage(page + 1) // Continue to the next page
            }

            // Create a new object with updated values
            const updatedResponseData = {
              ...response.data, // Copy all properties from the original response
              encounters: [],
              details: {
                ...response.data.details, // Copy all properties from the original details
                items,
                query_time: queryTime.toFixed(3),
                optimize_time: optimizeTime.toFixed(3),
              },
            }

            // Clear references to release memory.
            items = null
            queryTime = null
            optimizeTime = null

            // *** For testing only ***
            // const jsonString = JSON.stringify(updatedResponseData);
            // const objSizeBytes = new Blob([jsonString]).size;
            // const objSizeMB = (objSizeBytes / (1024 * 1024)).toFixed(2);
            // console.log(`Object size: ${objSizeMB } MB`);

            // All pages fetched, commit 'SET_API_DETAILS' mutation
            commit('SET_API_DETAILS', Vue.prototype.$custom.deepFreeze(updatedResponseData.details))
            commit('SET_BASE_DATA', updatedResponseData.baseData)
            commit('SET_SYNCED', true)

            // Flag last encounters sync pull as successful
            commit('isLastPullSuccess', true)
            commit('lastPullTimestamp', Date.now())

            return true
          }
          return response.data.message ? response.data.message : 'Download encounters - Unknown error'
        } catch (e) {
          // Retry logic: Fetch page with up to 2 retries for errors
          if (attempt < maxAttempts) {
            console.warn(`Error occurred. Retrying... Attempt ${attempt + 1} of 3`)
            return fetchPage(page, attempt + 1) // Retry the fetch on error
          } else {
            return Vue.prototype.$custom.processCommError(e, 'Download encounters')
          }
        }
      }

      return fetchPage(1) // Start fetching from page 1
    },
  },
}
